import React, { useState, useEffect, useRef, useCallback } from 'react';
import indianCities, { GUJARAT_CITIES, CITY_STATE } from '@/data/indianCities';

const gujaratSet = new Set(GUJARAT_CITIES);

const sortWithGujaratFirst = (filtered) => [
  ...filtered.filter((c) => gujaratSet.has(c)),
  ...filtered.filter((c) => !gujaratSet.has(c)),
];

const HighlightedName = ({ city, query }) => {
  const q = query.trim().toLowerCase();
  const lower = city.toLowerCase();
  if (!q || !lower.startsWith(q)) {
    return <span style={{ color: '#333' }}>{city}</span>;
  }
  const matchLen = q.length;
  return (
    <span>
      <span style={{ fontWeight: 600, color: '#E8651A' }}>{city.slice(0, matchLen)}</span>
      <span style={{ color: '#333' }}>{city.slice(matchLen)}</span>
    </span>
  );
};

const itemIcon = (city) => {
  if (city.includes('Airport')) return '✈️';
  if (gujaratSet.has(city)) return '🏛';
  return '📍';
};

const CityInput = ({
  label,
  placeholder,
  value = '',
  onChange,
  icon = '📍',
  required = false,
  error = '',
  inputId,
}) => {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  const updateSuggestions = useCallback((text) => {
    if (text.trim().length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      setHighlighted(-1);
      return;
    }
    const filtered = indianCities
      .filter((city) => city.toLowerCase().startsWith(text.toLowerCase()))
      .slice(0, 8);
    const sorted = sortWithGujaratFirst(filtered);
    setSuggestions(sorted);
    setShowDropdown(true);
    setHighlighted(sorted.length > 0 ? 0 : -1);
  }, []);

  const selectCity = (city) => {
    setQuery(city);
    onChange(city);
    setShowDropdown(false);
    setHighlighted(-1);
    setSuggestions([]);
  };

  const handleChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    onChange(text);
    updateSuggestions(text);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown) {
      if (e.key === 'ArrowDown' && query.trim().length >= 2) {
        updateSuggestions(query);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => (h < suggestions.length - 1 ? h + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => (h > 0 ? h - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlighted >= 0 && suggestions[highlighted]) {
        selectCity(suggestions[highlighted]);
      } else if (suggestions[0]) {
        selectCity(suggestions[0]);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setHighlighted(-1);
    } else if (e.key === 'Tab' && suggestions.length > 0) {
      selectCity(suggestions[0]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
        setHighlighted(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasError = Boolean(error);
  const id = inputId || `city-${label?.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
      {label && (
        <label
          htmlFor={id}
          className="font-body block"
          style={{ fontSize: '13px', color: '#555', fontWeight: 500, marginBottom: '6px' }}
        >
          {label}
          {required && ' *'}
        </label>
      )}

      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <span
          style={{
            position: 'absolute',
            left: '14px',
            color: '#E8651A',
            fontSize: '16px',
            pointerEvents: 'none',
            zIndex: 1,
          }}
          aria-hidden
        >
          {icon}
        </span>
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => query.trim().length >= 2 && updateSuggestions(query)}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          className="font-body"
          style={{
            width: '100%',
            height: '50px',
            padding: query ? '0 36px 0 40px' : '0 14px 0 40px',
            border: `1.5px solid ${hasError ? '#E53935' : '#E0E0E0'}`,
            borderRadius: '10px',
            fontSize: '14px',
            color: '#333',
            background: 'white',
            outline: 'none',
            boxShadow: hasError ? '0 0 0 3px rgba(229,57,53,0.1)' : 'none',
          }}
          onFocusCapture={(e) => {
            if (!hasError) {
              e.target.style.borderColor = '#E8651A';
              e.target.style.boxShadow = '0 0 0 3px rgba(232,101,26,0.1)';
            }
          }}
          onBlurCapture={(e) => {
            if (!hasError) {
              e.target.style.borderColor = '#E0E0E0';
              e.target.style.boxShadow = 'none';
            }
          }}
        />
        {query && (
          <button
            type="button"
            aria-label="Clear"
            onClick={() => {
              setQuery('');
              onChange('');
              setShowDropdown(false);
              inputRef.current?.focus();
            }}
            style={{
              position: 'absolute',
              right: '12px',
              background: 'none',
              border: 'none',
              color: '#AAA',
              fontSize: '18px',
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        )}
      </div>

      {showDropdown && query.trim().length >= 2 && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            background: 'white',
            border: '1.5px solid #E8651A',
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 1000,
            maxHeight: '280px',
            overflowY: 'auto',
            padding: '6px 0',
          }}
        >
          {suggestions.length === 0 ? (
            <div
              className="font-body"
              style={{ padding: '12px 16px', fontSize: '13px', color: '#AAA', fontStyle: 'italic' }}
            >
              No city found — type your city
            </div>
          ) : (
            suggestions.map((city, idx) => {
              const isHighlighted = idx === highlighted;
              return (
                <button
                  key={city}
                  type="button"
                  onMouseEnter={() => setHighlighted(idx)}
                  onClick={() => selectCity(city)}
                  className="font-body w-full text-left"
                  style={{
                    padding: '11px 16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '14px',
                    background: isHighlighted ? '#FFF5EE' : 'transparent',
                    border: 'none',
                    borderLeft: isHighlighted ? '3px solid #E8651A' : '3px solid transparent',
                  }}
                >
                  <span style={{ fontSize: '14px' }} aria-hidden>
                    {itemIcon(city)}
                  </span>
                  <HighlightedName city={city} query={query} />
                  <span
                    className="font-body"
                    style={{ fontSize: '11px', color: '#AAA', marginLeft: 'auto' }}
                  >
                    {CITY_STATE[city] || ''}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}

      {error && (
        <p
          className="font-body flex items-center gap-1"
          style={{ fontSize: '11px', color: '#E53935', marginTop: '4px' }}
        >
          <span aria-hidden>⚠</span> {error}
        </p>
      )}
    </div>
  );
};

export default CityInput;
